'use client'

import { Cell, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { EducationType } from '@marketplace-web/conversation-education/conversation-education-data'

import ProgressStepIconExperimental from '../../ProgressStepIconExperimental'

type Props = {
  type: EducationType
  price: string | null
}

type Step = {
  completed: boolean
  active: boolean
  title: string
  description: string
  testId?: string
}

const ElectronicsSteps = ({ type, price }: Props) => {
  const translate = useTranslate('conversation.education')

  const isEvsCheckVisible = [
    EducationType.InConversationSoldElectronicsEvs,
    EducationType.InConversationTransactionCompletedElectronicsEvs,
  ].includes(type)

  const isTransactionCompleted = [
    EducationType.InConversationTransactionCompletedElectronics,
    EducationType.InConversationTransactionCompletedElectronicsEvs,
  ].includes(type)

  const formatSoldStep = () => {
    let description = translate('steps.sold.description', { price })
    let testId = 'education-sold-step'

    if (isTransactionCompleted) {
      description = translate('steps.sold.completed_description')
      testId = 'education-sold-step-completed'
    }

    if (type === EducationType.InConversationSoldElectronicsEvs) {
      description = translate('steps.electronics.sold.evs_description')
      testId = 'education-sold-step-evs'
    }

    return {
      completed: true,
      active: false,
      title: translate('steps.sold.title'),
      description,
      testId,
    }
  }

  const formatSendItStep = () => {
    const completed = isTransactionCompleted
    const active = !isTransactionCompleted

    if (type === EducationType.InConversationSoldElectronics) {
      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.sold_title'),
        description: translate('steps.electronics.send_it.sold_body'),
        testId: 'education-send-it-step-sold-electronics',
      }
    }

    if (type === EducationType.InConversationSoldElectronicsEvs) {
      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.sold_evs_title'),
        description: translate('steps.electronics.send_it.sold_evs_body'),
        testId: 'education-send-it-step-sold-electronics-evs',
      }
    }

    if (type === EducationType.InConversationTransactionCompletedElectronics) {
      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.completed_title'),
        description: translate('steps.electronics.send_it.completed_body'),
        testId: 'education-send-it-step-completed-electronics',
      }
    }

    return {
      completed,
      active,
      title: translate('steps.electronics.send_it.completed_evs_title'),
      description: translate('steps.electronics.send_it.completed_evs_body'),
      testId: 'education-send-it-step-completed-electronics-evs',
    }
  }

  const formatEvsCheckStep = () => {
    if (type === EducationType.InConversationSoldElectronicsEvs) {
      return {
        completed: isTransactionCompleted,
        active: false,
        title: translate('steps.electronics.evs_sold_step.title'),
        description: translate('steps.electronics.evs_sold_step.body'),
        testId: 'education-evs-step-sold',
      }
    }

    return {
      completed: isTransactionCompleted,
      active: false,
      title: translate('steps.authenticity_check.title'),
      description: translate('steps.authenticity_check.completed_description'),
      testId: 'education-evs-step-completed',
    }
  }

  const formatGetPaidStep = () => ({
    completed: false,
    active: isTransactionCompleted,
    title: translate('steps.get_paid.title'),
    description: isTransactionCompleted
      ? translate('steps.get_paid.completed_description')
      : translate('steps.get_paid.description'),
    testId: 'education-get-paid-step-electronics',
  })

  const saleSteps = [
    formatSoldStep(),
    formatSendItStep(),
    ...(isEvsCheckVisible ? [formatEvsCheckStep()] : []),
    formatGetPaidStep(),
  ]

  const getStepIconStatus = (completed: boolean, active: boolean) => {
    if (completed) return ProgressStepIconExperimental.Status.Completed

    if (active) return ProgressStepIconExperimental.Status.Active

    return ProgressStepIconExperimental.Status.Inactive
  }

  const renderStepIcon = (completed: boolean, active: boolean, stepNumber: number) => (
    <ProgressStepIconExperimental
      status={getStepIconStatus(completed, active)}
      stepNumber={stepNumber}
    />
  )

  const renderStep = (step: Step, index: number) => {
    const textTheme = step.active || step.completed ? 'inherit' : 'muted'

    return (
      <Cell
        key={index}
        theme="transparent"
        prefix={renderStepIcon(step.completed, step.active, index + 1)}
        title={<Text as="span" text={step.title} theme={textTheme} testId={step.testId} />}
        body={<Text as="span" text={step.description} html theme={textTheme} />}
      />
    )
  }

  return <>{saleSteps.map(renderStep)}</>
}

export default ElectronicsSteps
